import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';

interface DayNavigationContextType {
  currentDate: string; // YYYY-MM-DD format
  setCurrentDate: (date: string) => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  goToToday: () => void;
  isToday: boolean;
  formatDisplayDate: (date: string) => string;
  // Card navigation persistence
  dashboardCardIndex: number;
  setDashboardCardIndex: (index: number) => void;
  foodLogCardIndex: number;
  setFoodLogCardIndex: (index: number) => void;
}

const DayNavigationContext = createContext<DayNavigationContextType | undefined>(undefined);

interface DayNavigationProviderProps {
  children: ReactNode;
}

// Helper function to get local date string in YYYY-MM-DD format
const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to create a Date object from YYYY-MM-DD string in local timezone
const createLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const DayNavigationProvider: React.FC<DayNavigationProviderProps> = ({ children }) => {
  const today = getLocalDateString(new Date());
  const [currentDate, setCurrentDateState] = useState<string>(today);
  const [dashboardCardIndex, setDashboardCardIndex] = useState<number>(0);
  const [foodLogCardIndex, setFoodLogCardIndex] = useState<number>(0);

  // Whether the app should follow the calendar: true while the user is on
  // "today", false once they pick a specific day. The date used to be set once
  // at mount and never rolled over, so an app left open overnight (or a phone
  // that resumes the page days later) kept showing — and logging to — the day
  // it was opened on.
  const followToday = useRef(true);
  const setCurrentDate = useCallback((date: string) => {
    followToday.current = date === getLocalDateString(new Date());
    setCurrentDateState(date);
  }, []);

  useEffect(() => {
    const check = () => {
      if (!followToday.current) return;
      const now = getLocalDateString(new Date());
      setCurrentDateState((prev) => (prev === now ? prev : now));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    const timer = setInterval(check, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      clearInterval(timer);
    };
  }, []);

  const goToPreviousDay = () => {
    const date = createLocalDate(currentDate);
    date.setDate(date.getDate() - 1);
    setCurrentDate(getLocalDateString(date));
  };

  const goToNextDay = () => {
    const date = createLocalDate(currentDate);
    date.setDate(date.getDate() + 1);
    setCurrentDate(getLocalDateString(date));
  };

  const goToToday = () => {
    setCurrentDate(getLocalDateString(new Date()));
  };

  const isToday = currentDate === getLocalDateString(new Date());

  const formatDisplayDate = (date: string): string => {
    const dateObj = createLocalDate(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateString = date; // Already in YYYY-MM-DD format
    const todayString = getLocalDateString(today);
    const yesterdayString = getLocalDateString(yesterday);
    const tomorrowString = getLocalDateString(tomorrow);

    if (dateString === todayString) return 'Today';
    if (dateString === yesterdayString) return 'Yesterday';
    if (dateString === tomorrowString) return 'Tomorrow';

    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const value: DayNavigationContextType = {
    currentDate,
    setCurrentDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    isToday,
    formatDisplayDate,
    dashboardCardIndex,
    setDashboardCardIndex,
    foodLogCardIndex,
    setFoodLogCardIndex
  };

  return (
    <DayNavigationContext.Provider value={value}>
      {children}
    </DayNavigationContext.Provider>
  );
};

export const useDayNavigation = (): DayNavigationContextType => {
  const context = useContext(DayNavigationContext);
  if (!context) {
    throw new Error('useDayNavigation must be used within a DayNavigationProvider');
  }
  return context;
}; 